import { cn } from '@amore/ui/cn'
import { Badge } from '@amore/ui/components/badge'
import { Checkbox } from '@amore/ui/components/checkbox'
import { Textarea } from '@amore/ui/components/textarea'
import { Link } from '@tanstack/react-router'
import { MapPin } from 'lucide-react'
import { useState } from 'react'
import { formatDate, plural, TODAY } from '@/lib/format'
import { supplierCategoryLabels } from '@/lib/labels'
import { useUpdate, useWedding } from '@/mock/store'
import type { ScheduleEvent, Supplier, WeddingData } from '@/mock/types'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { scheduleGroups, scheduleSignature } from './model'

const sendTask = 't-schedule-send'

type Recipient = {
  supplier: Supplier
  email: string | null
  events: ScheduleEvent[]
  state: { type: 'new' } | { type: 'sent'; sentAt: string } | { type: 'changed'; sentAt: string }
}

/** Every supplier who isn't cancelled and either handles an event or is already booked. */
function recipients(data: WeddingData): Recipient[] {
  const signature = scheduleSignature(data.schedule)
  const active = data.schedule.filter((event) => !event.removed)
  return data.suppliers
    .filter((supplier) => supplier.status !== 'cancelled')
    .map((supplier) => {
      const share = data.scheduleShares.find((item) => item.supplierId === supplier.id)
      return {
        supplier,
        email: supplier.contacts.find((contact) => contact.email)?.email ?? null,
        events: active.filter(
          (event) => event.owner?.type === 'supplier' && event.owner.id === supplier.id,
        ),
        state: !share
          ? { type: 'new' as const }
          : share.signature === signature
            ? { type: 'sent' as const, sentAt: share.sentAt }
            : { type: 'changed' as const, sentAt: share.sentAt },
      }
    })
    .filter((item) => item.events.length > 0 || ['agreed', 'booked'].includes(item.supplier.status))
}

const greeting = (data: WeddingData) =>
  `Dobrý den,\n\nposíláme harmonogram našeho svatebního dne. Události, které máte na starosti, jsou zvýrazněné. Kdyby vám něco nesedělo, ozvěte se nám prosím.\n\n${data.identity.brideName} a ${data.identity.groomName}`

/**
 * "Poslat dodavatelům": pick suppliers on the left, see exactly what the one
 * in focus gets on the right (the whole day, their events highlighted). The
 * send is remembered per supplier, so a later change shows up as "Změněno".
 */
export function SendSheet({ onClose }: { onClose: () => void }) {
  const data = useWedding()
  const update = useUpdate()
  const { showToast } = useToast()
  const list = recipients(data)
  const [selected, setSelected] = useState(
    () =>
      new Set(
        list
          .filter((item) => item.email && item.events.length && item.state.type !== 'sent')
          .map((item) => item.supplier.id),
      ),
  )
  const [focusId, setFocusId] = useState(
    () => list.find((item) => selected.has(item.supplier.id))?.supplier.id ?? list[0]?.supplier.id,
  )
  const [message, setMessage] = useState(() => greeting(data))
  const [error, setError] = useState(false)
  const focus = list.find((item) => item.supplier.id === focusId)
  const count = selected.size

  const toggle = (id: string) => {
    setError(false)
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  const send = () => {
    if (!count) {
      setError(true)
      return
    }
    const signature = scheduleSignature(data.schedule)
    const task = data.tasks.find((item) => item.id === sendTask && !item.done)
    update((current) => ({
      ...current,
      scheduleShares: [
        ...current.scheduleShares.filter((share) => !selected.has(share.supplierId)),
        ...[...selected].map((supplierId) => ({ supplierId, sentAt: TODAY, signature })),
      ],
      tasks: task
        ? current.tasks.map((item) => (item.id === task.id ? { ...item, done: true } : item))
        : current.tasks,
    }))
    showToast({
      title: 'Harmonogram odeslán',
      description: `${count} ${plural(count, 'dodavateli', 'dodavatelům', 'dodavatelům')}${
        task ? ` · úkol „${task.title}“ je hotový` : ''
      }`,
      tone: 'success',
    })
    onClose()
  }

  return (
    <DetailSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      width="wide"
      eyebrow="Harmonogram"
      title="Poslat dodavatelům"
      description="Každý dostane e-mailem celý průběh dne a své události zvýrazněné."
      primaryAction={{
        label: count
          ? `Poslat ${count} ${plural(count, 'dodavateli', 'dodavatelům', 'dodavatelům')}`
          : 'Poslat dodavatelům',
        onClick: send,
      }}
    >
      <div className="grid gap-8 md:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Komu</h3>
          {error ? (
            <p className="text-sm text-error-foreground">Vyberte aspoň jednoho dodavatele.</p>
          ) : null}
          <ul className="space-y-1">
            {list.map((item) => (
              <RecipientRow
                key={item.supplier.id}
                item={item}
                checked={selected.has(item.supplier.id)}
                focused={item.supplier.id === focusId}
                onToggle={() => toggle(item.supplier.id)}
                onFocus={() => setFocusId(item.supplier.id)}
              />
            ))}
          </ul>
          <label htmlFor="schedule-send-message" className="block space-y-2 pt-4">
            <span className="text-sm font-semibold">Zpráva pro všechny</span>
            <Textarea
              id="schedule-send-message"
              value={message}
              onChange={(change) => setMessage(change.target.value)}
              className="min-h-44 border-transparent bg-muted/60 text-sm hover:bg-muted focus:bg-card"
            />
          </label>
        </section>

        {focus ? <EmailPreview data={data} recipient={focus} message={message} /> : null}
      </div>
    </DetailSheet>
  )
}

function RecipientRow({
  item,
  checked,
  focused,
  onToggle,
  onFocus,
}: {
  item: Recipient
  checked: boolean
  focused: boolean
  onToggle: () => void
  onFocus: () => void
}) {
  const { supplier, email, events, state } = item
  const arrival = events.find((event) => event.time)?.time
  return (
    <li
      className={cn(
        'flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors',
        focused ? 'bg-panel-accent' : 'hover:bg-muted/60',
      )}
    >
      <Checkbox
        checked={checked}
        disabled={!email}
        onChange={onToggle}
        className="-my-2.5 shrink-0"
      >
        <span className="sr-only">Poslat: {supplier.name}</span>
      </Checkbox>
      <button type="button" onClick={onFocus} className="min-w-0 flex-1 text-left">
        <span className="block truncate text-sm font-semibold">{supplier.name}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {supplierCategoryLabels[supplier.category] ?? supplier.category}
          {events.length
            ? ` · ${events.length} ${plural(events.length, 'událost', 'události', 'událostí')}`
            : ' · bez vlastních událostí'}
          {arrival ? ` · od ${arrival}` : ''}
        </span>
        {email ? (
          <span className="mt-1 flex flex-wrap items-center gap-2">
            {state.type === 'new' ? (
              <span className="text-xs text-muted-foreground">Zatím neodesláno</span>
            ) : state.type === 'sent' ? (
              <Badge tone="success">Odesláno {formatDate(state.sentAt)}</Badge>
            ) : (
              <Badge tone="warning">Změněno po odeslání</Badge>
            )}
          </span>
        ) : (
          <span className="mt-1 block text-xs text-warning-foreground">
            Chybí e-mail.{' '}
            <Link to="/suppliers" className="font-semibold underline">
              Doplnit v dodavatelích
            </Link>
          </span>
        )}
      </button>
    </li>
  )
}

/** The e-mail one supplier gets: the message, then the whole day with their events highlighted. */
function EmailPreview({
  data,
  recipient,
  message,
}: {
  data: WeddingData
  recipient: Recipient
  message: string
}) {
  const { supplier, email, state } = recipient
  const groups = scheduleGroups(data).filter((group) => group.events.length)
  const mine = (event: ScheduleEvent) =>
    event.owner?.type === 'supplier' && event.owner.id === supplier.id
  const { brideName, groomName, date } = data.identity
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">Náhled e-mailu</h3>
      <div className="overflow-hidden rounded-panel border border-border bg-card">
        <dl className="grid grid-cols-[5rem_minmax(0,1fr)] gap-x-3 gap-y-1 border-b border-border bg-muted/40 px-5 py-3 text-sm">
          <dt className="text-muted-foreground">Komu</dt>
          <dd className="truncate">
            {supplier.name}
            {email ? ` <${email}>` : ''}
          </dd>
          <dt className="text-muted-foreground">Předmět</dt>
          <dd className="truncate">
            {state.type === 'changed' ? 'Aktualizace: ' : ''}Harmonogram svatby {brideName} a{' '}
            {groomName}
            {date ? ` · ${formatDate(date)}` : ''}
          </dd>
        </dl>
        <div className="space-y-6 px-5 py-5">
          <p className="whitespace-pre-line text-sm">{message}</p>
          <div className="space-y-5">
            {groups.map((group) => (
              <section key={group.id ?? 'prep'} className="space-y-1">
                <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {group.label}
                </h4>
                <ol>
                  {group.events.map((event) => (
                    <li
                      key={event.id}
                      className={cn(
                        'grid grid-cols-[3.25rem_minmax(0,1fr)] gap-3 rounded-lg px-2 py-1.5 text-sm',
                        mine(event) && 'bg-accent text-accent-foreground',
                      )}
                    >
                      <span className="tabular-nums font-semibold">{event.time ?? '—'}</span>
                      <span className="min-w-0">
                        <span className={cn('block', mine(event) && 'font-semibold')}>
                          {event.title}
                          {event.endTime ? (
                            <span className="font-normal text-muted-foreground">
                              {' '}
                              · do {event.endTime}
                            </span>
                          ) : null}
                        </span>
                        {event.location ? (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="size-3" aria-hidden="true" />
                            {event.location}
                          </span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
